import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Stack, Typography } from '@mui/material';
import { getUserApplications } from 'api/requests/userApi';
import styles from './Apps.module.scss';
import { useTranslation } from 'react-i18next';
import { Notification } from 'aura-react-components';
import { APPS_PAGE_TRANSLATION_KEYS } from './appsPageTranslationKeys';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faCloud, faWind } from '@fortawesome/free-solid-svg-icons';
import { isAirViewAppId, isOrionAppId, isResmedOnlineStoreAppId } from 'config/applicationsConfig';
import { logger } from 'utils/logger';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { APP_BOOTSTRAP_TO_REDIRECT_VITAL } from 'config/datadogVitals';
const getSafeLoginUrl = (url) => {
    if (!url) {
        return undefined;
    }
    try {
        const parsed = new URL(url);
        if (parsed.protocol === 'https:') {
            return parsed.toString();
        }
    }
    catch (_a) {
        // Invalid URL
    }
    return undefined;
};
export const Apps = () => {
    const [applications, setApplications] = useState([]);
    const [hasLoaded, setHasLoaded] = useState(false);
    const { t } = useTranslation('apps');
    const location = useLocation();
    const getAppVisualConfig = (id) => {
        if (isOrionAppId(id)) {
            return {
                icon: faWind,
                description: t(APPS_PAGE_TRANSLATION_KEYS.card.orionDescription)
            };
        }
        if (isResmedOnlineStoreAppId(id)) {
            return {
                icon: faCartShopping,
                description: t(APPS_PAGE_TRANSLATION_KEYS.card.resmedOnlineStoreDescription)
            };
        }
        return {
            icon: faCloud
        };
    };
    useEffect(() => {
        getUserApplications()
            .then((response) => {
            const apps = response.data.applications;
            if (apps) {
                const visibleApplications = apps.filter((app) => isOrionAppId(app.id) || isAirViewAppId(app.id));
                setApplications(visibleApplications);
                logger.info('Apps page: loaded applications', {
                    applications: visibleApplications.map((app) => ({
                        id: app.id,
                        name: app.displayName
                    }))
                });
            }
        })
            .catch((error) => {
            logger.error('Apps page: failed to load applications', { error });
        })
            .finally(() => {
            setHasLoaded(true);
        });
    }, []);
    useEffect(() => {
        var _a;
        const searchParams = new URLSearchParams((_a = location.search) !== null && _a !== void 0 ? _a : '');
        const shouldRedirect = searchParams.get('redirect') === 'true';
        if (!shouldRedirect) {
            datadogRum.stopDurationVital(APP_BOOTSTRAP_TO_REDIRECT_VITAL, {
                context: { redirected: false }
            });
            return;
        }
        if (!hasLoaded) {
            return;
        }
        if (applications.length !== 1) {
            datadogRum.stopDurationVital(APP_BOOTSTRAP_TO_REDIRECT_VITAL, {
                context: { redirected: false }
            });
            return;
        }
        const [app] = applications;
        const loginUrl = getSafeLoginUrl(app.loginUrl);
        if (!loginUrl) {
            datadogRum.stopDurationVital(APP_BOOTSTRAP_TO_REDIRECT_VITAL, {
                context: { redirected: false }
            });
            return;
        }
        datadogRum.stopDurationVital(APP_BOOTSTRAP_TO_REDIRECT_VITAL, {
            context: { redirected: true }
        });
        logger.info(`Automatically routing user from Account to ${app.name} at ${loginUrl}`, {
            applicationId: app.id,
            applicationName: app.name,
            destination: loginUrl
        });
        datadogRum.addAction('automatic redirect', {
            applicationId: app.id,
            applicationName: app.name,
            destination: loginUrl
        });
        globalThis.location.replace(loginUrl);
    }, [applications, hasLoaded, location.search]);
    if (hasLoaded && applications.length === 0) {
        return (_jsxs(Box, { children: [_jsxs(Stack, { spacing: 1, mb: 4, children: [_jsx(Typography, { variant: "h4", children: t(APPS_PAGE_TRANSLATION_KEYS.title) }), _jsx(Typography, { variant: "body1", color: "textSecondary", children: t(APPS_PAGE_TRANSLATION_KEYS.description) })] }), _jsx(Stack, { spacing: 3, children: _jsx(Notification, { id: "apps-empty-notification", severity: "warning", variant: "standard", icon: true, level: "page", header: t(APPS_PAGE_TRANSLATION_KEYS.emptyState.bannerTitle), message: t(APPS_PAGE_TRANSLATION_KEYS.emptyState.bannerMessage), className: styles.notification, notificationPropOverrides: {
                            alertPropOverrides: {
                                'aria-label': t(APPS_PAGE_TRANSLATION_KEYS.emptyState.bannerTitle),
                                sx: { borderRadius: '24px' }
                            }
                        } }) })] }));
    }
    return (_jsxs(Box, { children: [_jsxs(Stack, { spacing: 1, mb: 4, children: [_jsx(Typography, { variant: "h4", children: t(APPS_PAGE_TRANSLATION_KEYS.title) }), _jsx(Typography, { variant: "body1", color: "textSecondary", children: t(APPS_PAGE_TRANSLATION_KEYS.description) })] }), _jsx(Grid, { container: true, spacing: 4, children: applications.map((application) => {
                    const loginUrl = getSafeLoginUrl(application.loginUrl);
                    const { icon, description } = getAppVisualConfig(application.id);
                    const cardBody = (_jsx(CardContent, { children: _jsx(Stack, { spacing: 1.5, children: _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "flex-start", children: [_jsx(Box, { className: styles.iconCircle, "aria-hidden": "true", children: _jsx(FontAwesomeIcon, { icon: icon }) }), _jsxs(Box, { children: [_jsx(Typography, { variant: "h6", sx: { fontWeight: 600, fontSize: '1.1rem' }, children: application.displayName }), description && (_jsx(Typography, { variant: "body2", color: "textSecondary", children: description })), loginUrl && (_jsx(Typography, { variant: "body2", sx: { color: 'primary.main', fontWeight: 500, mt: 0.75 }, children: t(APPS_PAGE_TRANSLATION_KEYS.openApp) }))] })] }) }) }));
                    return (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(Card, { className: styles.card, children: loginUrl ? (_jsx(CardActionArea, { component: "a", href: loginUrl, target: "_blank", rel: "noopener noreferrer", children: cardBody })) : (cardBody) }) }, application.id));
                }) })] }));
};
